import { html, css } from 'lit';
import { button } from '@warp-ds/css/component-classes';
import WarpElement from '@warp-ds/elements-core';
import pulse from '@finn-no/pulse-sdk';
import { i18n } from '@lingui/core';

const converter = (value) => {
    if (!value) return null;

    try {
        const v = JSON.parse(value);
        if (Array.isArray(v)) {
            return v.map((val) => val.value || val);
        }
        if (v.value) {
            return [v.value];
        }
    } catch (err) {
        // noop
    }

    if (typeof value === 'string') {
        return [value];
    }

    return null;
};

class MeglersidenBanner extends WarpElement {
    static properties = {
        css: { attributes: true, converter },
    };

    static styles = [
        WarpElement.styles,
        css`
            *,:before,:after{--w-rotate:0;--w-rotate-x:0;--w-rotate-y:0;--w-rotate-z:0;--w-scale-x:1;--w-scale-y:1;--w-scale-z:1;--w-skew-x:0;--w-skew-y:0;--w-translate-x:0;--w-translate-y:0;--w-translate-z:0;box-sizing:border-box;border-style:solid;border-width:0;border-color:var(--w-s-color-border,var(--w-s-color-border-default))}html{font-size:62.5%}body{background-color:var(--w-s-color-background,var(--w-s-color-background-default));min-height:100%;margin:0;overflow-y:scroll}body,:host{-webkit-text-size-adjust:100%;tab-size:4;-webkit-tap-highlight-color:transparent;font-family:var(--w-font-family);font-size:var(--w-font-size-m);line-height:var(--w-line-height-m);color:var(--w-s-color-text,var(--w-s-color-text-default))}hr{color:inherit;border-top-width:1px;height:0}abbr:where([title]){-webkit-text-decoration:underline dotted;text-decoration:underline dotted}h1,h2,h3,h4,h5,h6{font-size:inherit;font-weight:700}a{cursor:pointer;color:var(--w-s-color-text-link);text-decoration:none}a:hover,a:focus,a:active{-webkit-text-decoration:var(--w-decoration-text-link);-webkit-text-decoration:var(--w-decoration-text-link);text-decoration:var(--w-decoration-text-link)}b,strong{font-weight:700}code,kbd,samp,pre{font-family:ui-monospace,SFMono-Regular,Menlo,Monaco,Consolas,Liberation Mono,Courier New,monospace;font-size:1em}sub,sup{vertical-align:baseline;font-size:75%;line-height:0;position:relative}sub{bottom:-.25em}sup{top:-.5em}table{text-indent:0;border-color:inherit;border-collapse:collapse}button,input,optgroup,select,textarea{font-family:inherit;font-size:100%;font-weight:inherit;line-height:inherit;color:inherit;margin:0;padding:0}button,select{text-transform:none}button,[type=button],[type=reset],[type=submit]{-webkit-appearance:button}:-moz-focusring{outline:auto}:-moz-ui-invalid{box-shadow:none}progress{vertical-align:baseline}::-webkit-inner-spin-button{height:auto}::-webkit-outer-spin-button{height:auto}[type=search]{-webkit-appearance:textfield;outline-offset:-2px}::-webkit-search-decoration{-webkit-appearance:none}::-webkit-file-upload-button{-webkit-appearance:button;font:inherit}summary{display:list-item}blockquote,dl,dd,h1,h2,h3,h4,h5,h6,hr,figure,p,pre{margin:0}fieldset{margin:0;padding:0}legend{padding:0}ol,ul,menu{margin:0;padding:0;list-style:none}textarea{resize:vertical}input::placeholder,textarea::placeholder{opacity:1;color:var(--w-s-color-text-placeholder)}button,[role=button]{cursor:pointer}:disabled{cursor:default}img,svg,video,canvas,audio,iframe,embed,object{vertical-align:middle;display:block}img,video{max-width:100%;height:auto}h1{font-size:var(--w-font-size-xxl);line-height:var(--w-line-height-xxl)}h2{font-size:var(--w-font-size-xl);line-height:var(--w-line-height-xl)}h3{font-size:var(--w-font-size-l);line-height:var(--w-line-height-l)}h4{font-size:var(--w-font-size-m);line-height:var(--w-line-height-m)}h5{font-size:var(--w-font-size-s);line-height:var(--w-line-height-s)}dt,dd{margin:0 16px}h1,h2,h3,h4,h5,ul,ol,dl,p,blockquote{margin:0 0 8px}[hidden]{display:none!important}[tabindex="-1"]:focus:not(:focus-visible){outline:none}legend{float:left;width:100%;margin:0;padding:0;display:table}legend+*{clear:both}fieldset{border:0;min-width:0;margin:0;padding:.01em 0 0}body:not(:-moz-handler-blocked) fieldset{display:table-cell}.page-container{background-color:var(--w-s-color-background);max-width:1010px;margin:0;padding:0 16px}.h3{font-weight:700;font-size:var(--w-font-size-l);line-height:var(--w-line-height-l)}@media (min-width:1300px){.page-container{margin-left:auto;margin-right:auto;padding-left:31px;padding-right:31px}}.rounded-8{border-radius:8px}.static{position:static}.s-bg-info-subtle{background-color:var(--w-s-color-background-info-subtle)}.mt-16{margin-top:1.6rem}.mt-8{margin-top:.8rem}.p-24{padding:2.4rem};
        `,
    ];

    trackBannerButtonClick(e) {
        pulse.trackEvent({
            type: 'Click',
            intent: 'Open',
            name: 'Meglersiden',
            object: {
                type: 'UIElement',
                elementType: 'Banner',
                position: 'MarketFrontPage',
            },
            target: {
                id: e.target.getAttribute('href'),
                type: 'Article',
                pageType: 'other',
                name: 'Sjekk ut Meglersiden',
            },
        });
    }

    connectedCallback() {
        super.connectedCallback();

        const locale = this.getAttribute('locale');
        const messages = JSON.parse(this.getAttribute('messages') || '{}');

        i18n.load(locale, messages);
        i18n.activate(locale);
    }

    render() {
        return html`
            <section
                class="s-bg-info-subtle page-container p-24 rounded-8  mt-16"
            >
                <h3 class="h3">
                    ${i18n.t({
            id: 'realestate.megler-banner.title',
            message: 'Find a realtor',
            comment: 'banner title for the meglersiden banner',
        })}
                </h3>
                <p>
                    ${i18n.t({
            id: 'realestate.megler-banner.text',
            message:
                'Looking for a realtor to sell your home? Check out Meglersiden and get offers from realtors in your area.',
            comment: 'banner text for the meglersiden banner',
        })}
                </p>
                <a
                    target="_blank"
                    class="${button.secondary} mt-8"
                    href="https://meglersiden.no/?utm_source=finn"
                    @click=${this.trackBannerButtonClick}
                >
                    ${i18n.t({
            id: 'realestate.megler-banner.link',
            message: 'Check out Meglersiden',
            comment: 'link, used as button, links to meglersiden',
        })}
                </a>
            </section>
        `;
    }
}

customElements.define('meglersiden-banner', MeglersidenBanner);
